import { capitalize } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from 'sonner';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import CustomSpinner from '../../../loader/CustomSpinner';
import { setSelectedView } from '../../../redux/providerProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { ProviderService } from '../../../services/providers/providerService';
import { ProviderApiResponse } from '../../../type/entities/provider-type';
import { ActionIcon, Tooltip } from 'rizzui';
import StatusDropdown from '../../../components/forms/StatusDropdown';
import { providerStatuses } from '../../../utils/SelectorMapping';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.providers.profile.info,
        permission: {
            group: 'provider',
            action: 'get',
        },
    },
    {
        label: 'Sedes',
        path: menuRoutes.providers.profile.headquarters,
        permission: {
            group: 'headquarter',
            action: 'list',
        },
    }
];

export default function ProviderProfileLayout({ isLoading }: { isLoading?: boolean }) {

    // HOOKS
    const [changingStatus, setChangingStatus] = useState<string[]>([]);

    const { selectedView } = useSelector((state: RootState) => state.providerProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const service = new ProviderService();

    // DATA FETCHING

    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN FETCHES PROVIDER DATA BY ID.
     *  @ES OBTIENE LOS DATOS DE UN PROVEEDOR POR SU ID.
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loadingProvider, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getProvideById(id);
        return response.getResponseData() as ProviderApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    const pageHeader = {
        title: `Perfil de ${data?.companyName || 'Proveedor'}`,
        breadcrumb: [
            { name: 'Proveedores', href: menuRoutes.providers.path },
            { name: 'Perfil de Proveedor' },
        ],
    };

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN HANDLES THE DELETION OF A PROVIDER BY ITS ID.
     *  @ES MUESTRA UNA CONFIRMACIÓN PARA ELIMINAR UN PROVEEDOR POR SU ID.
     */
    //--------------------------------------------------------------------------------------------------------------    
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteProvider(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.providers.path);
                toast.success('Proveedor eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL CAMBIO DE ESTADO DEL PROVEEDOR
     * @EN HANDLE THE CHANGE OF PROVIDER STATUS
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleStatusChange = async (id: string, status: string) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleProviderStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(response.message);
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.providers.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {data && (
                        <div className='flex flex-col gap-2 justify-content-center text-center'>
                            <StatusDropdown
                                entityId={id}
                                title="¿Estás seguro de cambiar el estado del proveedor?"
                                statesOptions={providerStatuses}
                                currentState={{
                                    name: capitalize(data?.status?.name),
                                    id: data?.status?.id,
                                    color: data?.status?.color
                                }}
                                handleStateChange={(entityId: string, newStateId: string) => handleStatusChange(entityId, newStateId)}
                                disabled={changingStatus.includes(id)}
                                hasComments={false}
                            />
                        </div>
                    )}

                    {/* TODO: things */}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <div className='m-auto h-full'><CustomSpinner /></div> : <Outlet />}
        </>
    );
}